import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import {
  Box,
  CustomButton,
  CustomDatePicker,
  TitleComponent,
} from "ui-components";
import { formatLargeNumber, formatCount } from "ui-lib";
import { getAccumulatedFunding } from "ui-io/queries/get-accumulated-funding";
import {
  companiesMutation,
  CompanyResultT,
} from "ui-io/queries/get-filtered-companies";

import { HorizontalCard, Amount, Label } from "../../components/common";
import WeeklyOverview from "../../components/common/graph/weekly-overview";
import Company from "../company";
import FounderTable from "../founder-table";
import LatestRegisteredCompanies from "../latest-registered-companies";
import ValueWithSkeleton from "../../components/common/value-with-skeleton";
import PremiumBanner from "../../components/common/premium-banner";
import { MarketCapT, companyCategories } from "ui-io/common-io";
import DealBy from "../investment-trends/deal-by";
import TopIndustries from "../investment-trends/top-industries";
import {useWindowWidth} from "ui-lib";

const DashboardPage = () => {
  const windowWidth = useWindowWidth();
  const [selectedYear, setSelectYear] = useState<{
    label: string;
    value: string;
  }>({
    label: "All",
    value: "All",
  });
  const [marketCap, setMarketCap] = useState<MarketCapT | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyResultT>();

  const accumulatedFundingConnection = useQuery(
    getAccumulatedFunding({
      year: Number(selectedYear?.value),
      marketCapital: marketCap,
    }),
  );

  const companyMutation = useMutation(companiesMutation, {
    onSuccess: (response: CompanyResultT) => {
      setCompanyData(response);
    },
    onError: () => {
      // setCompanyData(null);
    },
  });

  // Fetch on mount if no customData
  useEffect(() => {
    companyMutation.mutate({
      filterObject: undefined,
      page: 0,
      pageSize: 5,
    });
  }, []);

  const accumulatedFunding: Record<string, any> = useMemo(() => {
    if (!accumulatedFundingConnection?.data) {
      return {};
    }

    const data = accumulatedFundingConnection.data;
    return {
      fundedAmount: data.fundedAmount,
      fundingRounds: data.fundingRounds,
      mostFundedStage: data.mostFundedStage,
      companyCount: data.companyCount,
    };
  }, [accumulatedFundingConnection.data]);
  const changeCompanyFilter = (type: MarketCapT) => {
    setMarketCap((prev) => (prev === type ? undefined : type));
  };

  return (
    <>
      <PremiumBanner />
      <Box flexDir="row" justify="between" mb="medium">
        <Box flexDir="row" justify="center" items="center" gap={"large"}>
          <TitleComponent marginTop="0px">
            CY {selectedYear.value}
          </TitleComponent>{" "}
          <Box flexDir="row" gap="small">
            {companyCategories.map(
              ({
                key,
                label,
                icon,
              }: {
                key: MarketCapT;
                label: string;
                icon: string;
              }) => {
                const isActive = marketCap === key;
                return (
                  <CustomButton
                    key={key}
                    icon={isActive ? ("small-cross" as any) : (icon as any)}
                    intent={isActive && marketCap == key ? "primary" : "none"}
                    onClick={() => changeCompanyFilter(key)}
                    disabled
                  >
                    {label}
                  </CustomButton>
                );
              },
            )}
          </Box>
        </Box>

        <div className="position-relative">
          <CustomButton
            onClick={() => setOpen(!open)}
            rightIcon="caret-down"
            bradius="5px"
            width="100px"
          >
            {selectedYear.value}
          </CustomButton>
          {open ? (
            <div className="inline-datepicker">
              <CustomDatePicker
                mode={"in_year"}
                yearItemNumber={10}
                value={selectedYear.value}
                showIcon={false}
                inline={true}
                onValueChange={(dates) => {
                  const year: string | undefined = dates[0]
                    ?.getFullYear()
                    .toString();
                  if (year) {
                    setSelectYear({ label: year, value: year });
                  }
                  setOpen(false);
                }}
              />
            </div>
          ) : null}
        </div>
      </Box>
      <Box flexDir="row" gap={windowWidth > 1520 ? "xxlarge" : windowWidth > 1280 ? "xlarge" : "large"} minWidth={"1"}>
        <HorizontalCard
          avatarIcon={"/images/funding-round.svg"}
          className="pt-xlg pb-xlg"
          imageWidth={"60"}
        >
          <Label>Funding Rounds</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.fundingRounds || "550"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/total-funding.svg"}
          className="pt-xlg pb-xlg"
          imageWidth={"60"}
        >
          <Label>Total Funding</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.fundedAmount
                  ? `£${formatLargeNumber(accumulatedFunding.fundedAmount)}`
                  : "10.73 Bn"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/avg-round.svg"}
          className="pt-xlg pb-xlg"
          imageWidth={"60"}
        >
          <Label>Most Funded Stage</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.mostFundedStage || "Early Stage"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/total-companies.svg"}
          className="pt-xlg pb-xlg"
          imageWidth={"60"}
        >
          <Label>Funded Companies</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.companyCount
                  ? formatCount(accumulatedFunding.companyCount)
                  : "12000+"}
              </Amount>
            }
          />
        </HorizontalCard>
      </Box>

      <Box flexDir="row" gap="xxlarge">
        <Box width={"1"} mt={"small"}>
          <WeeklyOverview
            year={selectedYear?.value}
            marketCapital={marketCap}
          />
        </Box>
      </Box>
      <Box mt={"xlarge"}>
        <DealBy />
      </Box>
      <Box mt={"xlarge"}>
        <Company
          companyData={companyData}
          companyMutation={companyMutation}
          title={
            <TitleComponent className="fs-lg ml-xs">
              Top Funded Companies
            </TitleComponent>
          }
          pageSize={5}
        />
      </Box>
      <Box mt={"xlarge"}>
        <FounderTable />
      </Box>
      <Box mt={"xlarge"}>
        <LatestRegisteredCompanies />
      </Box>
      <Box mt={"xlarge"}>
        <TopIndustries year={selectedYear?.value} />
      </Box>
    </>
  );
};
export default DashboardPage;
